/**
 * @fileoverview Componente responsável por exibir QR Codes e pairing codes de autenticação WAHA.
 */

import React, { useEffect, useState, useContext, useRef } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopyOutlined as CopyIcon } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";

import { Check, RefreshCw } from "lucide-react";
const PRIMARY = "#3D5CFD";
const PRIMARY_DARK = "#2B46D4";
const PRIMARY_SOFT = "#EEF2FF";
const INK = "#0F172A";
const INK_MUTED = "#475569";
const LINE = "#E2E8F0";
const SUCCESS = "#10B981";

const useStyles = makeStyles({
  dialogPaper: {
    borderRadius: 20,
    overflow: "hidden",
    width: 760,
    maxWidth: "calc(100% - 32px)",
    background: "#ffffff",
    boxShadow:
      "0 40px 80px -24px rgba(15, 23, 42, 0.28), 0 12px 28px -12px rgba(15, 23, 42, 0.14)",
  },
  header: {
    padding: "28px 32px 18px",
    display: "flex",
    justifyContent: "center",
  },
  modeSwitch: {
    position: "relative",
    display: "inline-flex",
    padding: 4,
    background: "#F1F5F9",
    borderRadius: 999,
    gap: 0,
  },
  modeIndicator: {
    position: "absolute",
    top: 4,
    bottom: 4,
    left: 4,
    width: "calc(50% - 4px)",
    borderRadius: 999,
    background: PRIMARY,
    boxShadow: "0 6px 14px -4px rgba(61, 92, 253, 0.55)",
    transition:
      "transform 300ms cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 300ms ease",
    zIndex: 1,
  },
  modeButton: {
    position: "relative",
    zIndex: 2,
    border: "none",
    background: "transparent",
    minWidth: 150,
    padding: "9px 22px",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.01em",
    color: INK_MUTED,
    cursor: "pointer",
    borderRadius: 999,
    transition: "color 200ms ease",
    fontFamily: "inherit",
    "&:focus-visible": {
      outline: `2px solid ${PRIMARY}`,
      outlineOffset: 3,
    },
  },
  modeButtonActive: {
    color: "#ffffff",
  },
  divider: {
    height: 1,
    margin: "0 32px",
    background: `linear-gradient(90deg, transparent, ${LINE} 18%, ${LINE} 82%, transparent)`,
  },
  body: {
    padding: "28px 32px 8px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 36,
    alignItems: "start",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    color: PRIMARY,
    margin: 0,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: INK,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: INK_MUTED,
    lineHeight: 1.55,
    margin: 0,
  },
  stepList: {
    listStyle: "none",
    margin: "6px 0 0",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  step: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  },
  stepNumber: {
    flex: "0 0 26px",
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: PRIMARY_SOFT,
    color: PRIMARY,
    fontSize: 12,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: 0,
  },
  stepText: {
    fontSize: 14,
    color: "#334155",
    lineHeight: 1.5,
    paddingTop: 3,
    minWidth: 0,
  },
  stepIcon: {
    display: "inline-flex",
    verticalAlign: "-3px",
    width: 15,
    height: 15,
    fill: "#64748B",
    margin: "0 3px",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
  },
  qrFrame: {
    position: "relative",
    padding: 14,
    background: "#ffffff",
    border: `1px solid ${LINE}`,
    borderRadius: 18,
    boxShadow: "0 1px 0 rgba(15, 23, 42, 0.02), 0 10px 30px -18px rgba(61, 92, 253, 0.35)",
  },
  qrCorner: {
    position: "absolute",
    width: 18,
    height: 18,
    borderColor: PRIMARY,
    borderStyle: "solid",
    borderWidth: 0,
  },
  qrCornerTL: { top: 6, left: 6, borderTopWidth: 2, borderLeftWidth: 2, borderTopLeftRadius: 6 },
  qrCornerTR: { top: 6, right: 6, borderTopWidth: 2, borderRightWidth: 2, borderTopRightRadius: 6 },
  qrCornerBL: { bottom: 6, left: 6, borderBottomWidth: 2, borderLeftWidth: 2, borderBottomLeftRadius: 6 },
  qrCornerBR: { bottom: 6, right: 6, borderBottomWidth: 2, borderRightWidth: 2, borderBottomRightRadius: 6 },
  qrPlaceholder: {
    width: 256,
    height: 256,
    display: "grid",
    placeItems: "center",
    color: "#94A3B8",
    fontSize: 13,
    fontWeight: 500,
    background:
      "repeating-linear-gradient(45deg, #F8FAFC 0 10px, #F1F5F9 10px 20px)",
    borderRadius: 10,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: PRIMARY,
    boxShadow: `0 0 0 0 ${PRIMARY}`,
    animation: "$pulse 1.6s ease-in-out infinite",
  },
  statusText: {
    fontSize: 12,
    color: INK_MUTED,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 500,
  },
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 0.45,
      transform: "scale(0.9)",
      boxShadow: `0 0 0 0 rgba(61, 92, 253, 0.4)`,
    },
    "50%": {
      opacity: 1,
      transform: "scale(1)",
      boxShadow: `0 0 0 6px rgba(61, 92, 253, 0)`,
    },
  },
  pairingForm: {
    width: 296,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: INK_MUTED,
    marginBottom: 6,
  },
  phoneField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#ffffff",
      fontSize: 15,
      fontFamily:
        '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      letterSpacing: "0.02em",
      color: INK,
      transition: "box-shadow 200ms ease",
    },
    "& .MuiOutlinedInput-input": {
      color: INK,
      "&::placeholder": {
        color: "#94A3B8",
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#CBD5E1",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PRIMARY,
      borderWidth: 1.5,
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(61, 92, 253, 0.12)",
    },
    "& .MuiFormHelperText-root": {
      fontSize: 12,
      color: INK_MUTED,
      marginLeft: 2,
      marginTop: 6,
    },
    "& .MuiFormHelperText-root.Mui-error": { color: "#DC2626" },
  },
  generateButton: {
    textTransform: "none",
    background: PRIMARY,
    color: "#ffffff",
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: "0.005em",
    padding: "11px 18px",
    borderRadius: 12,
    boxShadow: "0 10px 22px -8px rgba(61, 92, 253, 0.55)",
    transition: "background 180ms ease, box-shadow 180ms ease, transform 120ms ease",
    "&:hover": {
      background: PRIMARY_DARK,
      boxShadow: "0 12px 26px -8px rgba(61, 92, 253, 0.65)",
    },
    "&:active": { transform: "translateY(1px)" },
    "&.Mui-disabled": {
      background: "#CBD5E1",
      color: "#ffffff",
      boxShadow: "none",
    },
  },
  codeCard: {
    width: "100%",
    padding: "22px 20px 20px",
    borderRadius: 16,
    background: `linear-gradient(180deg, #ffffff 0%, ${PRIMARY_SOFT} 100%)`,
    border: `1px solid rgba(61, 92, 253, 0.18)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    animation: "$codeReveal 340ms cubic-bezier(0.2, 0.9, 0.3, 1.15)",
  },
  "@keyframes codeReveal": {
    "0%": { opacity: 0, transform: "scale(0.94) translateY(6px)" },
    "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
  },
  codeLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: PRIMARY,
  },
  codeValue: {
    fontFamily:
      '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 32,
    fontWeight: 700,
    color: INK,
    letterSpacing: "0.22em",
    fontVariantNumeric: "tabular-nums",
    display: "flex",
    alignItems: "center",
    gap: 6,
    userSelect: "all",
  },
  codeSeparator: {
    display: "inline-block",
    width: 14,
    height: 2,
    background: "#CBD5E1",
    borderRadius: 2,
    margin: "0 6px",
  },
  copyButton: {
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    color: PRIMARY,
    letterSpacing: "0.005em",
    padding: "6px 16px",
    borderRadius: 999,
    border: `1px solid rgba(61, 92, 253, 0.28)`,
    background: "#ffffff",
    transition: "all 180ms ease",
    "&:hover": {
      background: PRIMARY_SOFT,
      borderColor: PRIMARY,
    },
  },
  copyButtonDone: {
    color: SUCCESS,
    borderColor: "rgba(16, 185, 129, 0.4)",
    background: "#ECFDF5",
    "&:hover": {
      background: "#D1FAE5",
      borderColor: SUCCESS,
    },
  },
  actions: {
    padding: "16px 32px 22px",
    justifyContent: "space-between",
    background: "#ffffff",
  },
  ghostButton: {
    textTransform: "none",
    color: PRIMARY,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    "&:hover": { background: PRIMARY_SOFT },
    "&.Mui-disabled": { color: "#94A3B8" },
  },
  closeButton: {
    textTransform: "none",
    color: INK_MUTED,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 18px",
    borderRadius: 10,
    "&:hover": { background: "#F1F5F9", color: INK },
  },
});

// Remove prefixos "N - " / "N- " das strings i18n existentes, já que usamos pílulas numeradas.
const stripStepPrefix = (value) =>
  typeof value === "string" ? value.replace(/^\s*\d+\s*-\s*/, "") : value;

const AndroidDotsIcon = ({ className }) => (
  <svg
    focusable="false"
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
  >
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const GearIcon = ({ className }) => (
  <svg
    focusable="false"
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
  >
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();

  const [qrCode, setQrCode] = useState("");
  const [startTime] = useState(Date.now());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const latestQrCodeRef = useRef("");

  const [authMode, setAuthMode] = useState("qr");
  const [phoneInput, setPhoneInput] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState("");
  const [pairingCopied, setPairingCopied] = useState(false);
  const phoneInputRef = useRef(null);

  const socketManager = useContext(SocketContext);
  const normalizeStatus = (status) => String(status || "").toUpperCase();

  const shouldStartWahaSession = (data) => {
    const normalized = normalizeStatus(data?.status);
    const isConnected = normalized === "CONNECTED" || normalized === "WORKING";
    const isStarting = normalized === "OPENING" || normalized === "STARTING";
    const isQrState = normalized === "QRCODE" || normalized === "SCAN_QR_CODE";
    return !isConnected && !isStarting && !isQrState;
  };

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) return;
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
        latestQrCodeRef.current = data.qrcode || "";

        if (data.provider !== "waha") return;

        const normalized = normalizeStatus(data.status);
        const isQrState = normalized === "QRCODE" || normalized === "SCAN_QR_CODE";

        if ((!data.qrcode && isQrState) || shouldStartWahaSession(data)) {
          await api.post(`/whatsapp/${whatsAppId}/generate-qr`);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId, open]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleWhatsappSession = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        const elapsed = Date.now() - startTime;
        console.log(`[QR PERFORMANCE] QR Code received in ${elapsed}ms`);
        setQrCode(data.session.qrcode);
        latestQrCodeRef.current = data.session.qrcode || "";
        setIsRegenerating(false);
      }

      if (
        data.action === "update" &&
        data.session.id === whatsAppId &&
        data.session.status === "CONNECTED"
      ) {
        onClose();
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, handleWhatsappSession);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, handleWhatsappSession);
    };
  }, [whatsAppId, onClose, socketManager, startTime]);

  useEffect(() => {
    if (!whatsAppId || !open) return undefined;

    let qrRefreshInterval = setInterval(async () => {
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);

        if (
          data.qrcode &&
          data.status === "qrcode" &&
          data.qrcode !== latestQrCodeRef.current
        ) {
          setQrCode(data.qrcode);
          latestQrCodeRef.current = data.qrcode;
        }

        if (data.status === "CONNECTED") {
          clearInterval(qrRefreshInterval);
          qrRefreshInterval = null;
        }
      } catch (err) {
        console.error("[QR AUTO-REFRESH] Erro ao atualizar QR Code:", err);
      }
    }, 3000);

    return () => {
      if (qrRefreshInterval) clearInterval(qrRefreshInterval);
    };
  }, [whatsAppId, open]);

  useEffect(() => {
    if (!open) {
      setAuthMode("qr");
      setPhoneInput("");
      setPairingCode("");
      setPairingError("");
      setPairingCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (authMode === "pairing" && phoneInputRef.current) {
      const t = setTimeout(() => phoneInputRef.current?.focus(), 280);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [authMode]);

  const handleRegenerateQR = async () => {
    try {
      setIsRegenerating(true);
      const { data } = await api.get(`/whatsapp/${whatsAppId}`);

      if (data.qrcode) {
        setQrCode(data.qrcode);
        latestQrCodeRef.current = data.qrcode;
      } else if (data.provider === "waha") {
        const normalized = normalizeStatus(data.status);
        const isQrState = normalized === "QRCODE" || normalized === "SCAN_QR_CODE";
        if (isQrState || shouldStartWahaSession(data)) {
          await api.post(`/whatsapp/${whatsAppId}/generate-qr`);
        }
      }
      setIsRegenerating(false);
    } catch (err) {
      setIsRegenerating(false);
      toastError(err);
    }
  };

  const selectMode = (newMode) => {
    if (newMode === authMode) return;
    setAuthMode(newMode);
    setPairingError("");
    setPairingCopied(false);
    if (newMode === "qr") setPairingCode("");
  };

  const handleRequestPairingCode = async () => {
    const digitsOnly = phoneInput.replace(/\D/g, "");
    if (digitsOnly.length < 10) {
      setPairingError(i18n.t("qrCodeModal.pairing.invalidPhone"));
      return;
    }

    try {
      setPairingLoading(true);
      setPairingError("");
      setPairingCopied(false);
      const { data } = await api.post(
        `/whatsapp/${whatsAppId}/request-pairing-code`,
        { phoneNumber: digitsOnly }
      );
      setPairingCode(data.code || "");
    } catch (err) {
      toastError(err);
      setPairingCode("");
    } finally {
      setPairingLoading(false);
    }
  };

  const handleCopyPairingCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setPairingCopied(true);
      setTimeout(() => setPairingCopied(false), 2000);
    } catch (err) {
      console.error("[QR PAIRING] Falha ao copiar:", err);
    }
  };

  const renderStepTwo = () => (
    <>
      {stripStepPrefix(i18n.t("qrCodeModal.steps.two.partOne"))}
      <AndroidDotsIcon className={classes.stepIcon} />
      {i18n.t("qrCodeModal.steps.two.partTwo")}
      <GearIcon className={classes.stepIcon} />
      {i18n.t("qrCodeModal.steps.two.partThree")}
    </>
  );

  const qrSteps = [
    stripStepPrefix(i18n.t("qrCodeModal.steps.one")),
    renderStepTwo(),
    stripStepPrefix(i18n.t("qrCodeModal.steps.three")),
    stripStepPrefix(i18n.t("qrCodeModal.steps.four")),
  ];

  const renderCodeValue = () => {
    if (!pairingCode) return null;
    const parts = pairingCode.split("-");
    return parts.map((part, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span className={classes.codeSeparator} aria-hidden="true" />}
        <span>{part}</span>
      </React.Fragment>
    ));
  };

  const renderQrPanel = () => (
    <div className={classes.rightColumn}>
      <div className={classes.qrFrame}>
        <span className={`${classes.qrCorner} ${classes.qrCornerTL}`} />
        <span className={`${classes.qrCorner} ${classes.qrCornerTR}`} />
        <span className={`${classes.qrCorner} ${classes.qrCornerBL}`} />
        <span className={`${classes.qrCorner} ${classes.qrCornerBR}`} />
        {qrCode ? (
          qrCode.startsWith("data:image") ? (
            <img
              src={qrCode}
              alt="QR Code"
              style={{ width: 256, height: 256, display: "block" }}
            />
          ) : (
            <QRCode value={qrCode} size={256} level="M" renderAs="svg" />
          )
        ) : (
          <div className={classes.qrPlaceholder}>
            {i18n.t("qrCodeModal.waiting")}
          </div>
        )}
      </div>
      <span className={classes.statusText}>
        <span className={classes.liveDot} />
        {i18n.t("qrCodeModal.waiting")}
      </span>
    </div>
  );

  const renderPairingPanel = () => (
    <div className={classes.rightColumn}>
      <div className={classes.pairingForm}>
        <div>
          <div className={classes.fieldLabel}>
            {i18n.t("qrCodeModal.pairing.phoneLabel")}
          </div>
          <TextField
            inputRef={phoneInputRef}
            className={classes.phoneField}
            placeholder={i18n.t("qrCodeModal.pairing.phonePlaceholder")}
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              if (pairingError) setPairingError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !pairingLoading) {
                e.preventDefault();
                handleRequestPairingCode();
              }
            }}
            variant="outlined"
            size="small"
            fullWidth
            error={Boolean(pairingError)}
            helperText={
              pairingError ||
              i18n.t("qrCodeModal.pairing.phonePlaceholder")
            }
            inputProps={{ inputMode: "numeric", "aria-label": "phone number" }}
          />
        </div>
        <Button
          className={classes.generateButton}
          variant="contained"
          disableElevation
          onClick={handleRequestPairingCode}
          disabled={pairingLoading}
          startIcon={
            pairingLoading ? (
              <CircularProgress size={15} color="inherit" />
            ) : null
          }
          fullWidth
        >
          {pairingLoading
            ? i18n.t("qrCodeModal.pairing.generating")
            : i18n.t("qrCodeModal.pairing.generate")}
        </Button>

        {pairingCode && (
          <div className={classes.codeCard}>
            <span className={classes.codeLabel}>
              {i18n.t("qrCodeModal.pairing.title")}
            </span>
            <div className={classes.codeValue} aria-label={pairingCode}>
              {renderCodeValue()}
            </div>
            <Button
              className={`${classes.copyButton} ${
                pairingCopied ? classes.copyButtonDone : ""
              }`}
              size="small"
              startIcon={
                pairingCopied ? (
                  <Check size={18} />
                ) : (
                  <CopyIcon fontSize="small" />
                )
              }
              onClick={handleCopyPairingCode}
            >
              {pairingCopied
                ? i18n.t("qrCodeModal.pairing.copied")
                : i18n.t("qrCodeModal.pairing.copy")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <div className={classes.modeSwitch} role="tablist">
          <span
            className={classes.modeIndicator}
            style={{
              transform:
                authMode === "qr" ? "translateX(0)" : "translateX(100%)",
            }}
            aria-hidden="true"
          />
          <button
            type="button"
            role="tab"
            aria-selected={authMode === "qr"}
            className={`${classes.modeButton} ${
              authMode === "qr" ? classes.modeButtonActive : ""
            }`}
            onClick={() => selectMode("qr")}
          >
            {i18n.t("qrCodeModal.tabs.qr")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={authMode === "pairing"}
            className={`${classes.modeButton} ${
              authMode === "pairing" ? classes.modeButtonActive : ""
            }`}
            onClick={() => selectMode("pairing")}
          >
            {i18n.t("qrCodeModal.tabs.pairing")}
          </button>
        </div>
      </div>

      <div className={classes.divider} />

      <DialogContent style={{ padding: 0, overflow: "visible" }}>
        <div className={classes.body}>
          <div className={classes.leftColumn}>
            <Typography component="p" className={classes.eyebrow}>
              {authMode === "qr" ? "WhatsApp Web" : "Pairing"}
            </Typography>
            <Typography component="h2" className={classes.title}>
              {authMode === "qr"
                ? i18n.t("qrCodeModal.title")
                : i18n.t("qrCodeModal.pairing.title")}
            </Typography>

            {authMode === "qr" ? (
              <ol className={classes.stepList}>
                {qrSteps.map((text, idx) => (
                  <li key={idx} className={classes.step}>
                    <span className={classes.stepNumber}>{idx + 1}</span>
                    <span className={classes.stepText}>{text}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <Typography component="p" className={classes.subtitle}>
                {i18n.t("qrCodeModal.pairing.instructions")}
              </Typography>
            )}
          </div>

          {authMode === "qr" ? renderQrPanel() : renderPairingPanel()}
        </div>
      </DialogContent>

      <div className={classes.divider} style={{ marginTop: 20 }} />

      <DialogActions className={classes.actions}>
        {authMode === "qr" ? (
          <Button
            className={classes.ghostButton}
            onClick={handleRegenerateQR}
            disabled={isRegenerating}
            startIcon={
              isRegenerating ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RefreshCw size={18} />
              )
            }
          >
            {isRegenerating ? "Atualizando..." : "Atualizar QR Code"}
          </Button>
        ) : (
          <span />
        )}
        <Button className={classes.closeButton} onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
